import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Lock, Menu, ScrollText } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useMe } from '@/features/auth/hooks';
import { useOnboardingState } from '@/features/dashboard/hooks';
import { cardGate, navTabLocked, termsGateActive } from '@/features/dashboard/onboarding';
import { cn } from '@/lib/utils';
import { visibleNavGroups } from './nav-items';

/**
 * MobileNav — hamburger + left drawer for below-`lg` viewports, where the
 * Sidebar rail is hidden. Same nav data and the same onboarding
 * gating as the rail (one shared hook), so the two can never disagree on
 * what's locked. Touch has no hover, so each item shows its description
 * as a sub-line instead of the rail's ⓘ tooltip. The drawer closes on
 * navigation.
 */
export function MobileNav() {
  const [open, setOpen] = useState(false);
  const onboarding = useOnboardingState();
  // Owner-only nav items are filtered out for everyone else.
  const me = useMe();
  // Mirror the rail's footer: a Terms review link for already-accepted users.
  const showTermsLink = !termsGateActive(onboarding);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Open navigation menu"
          className="lg:hidden inline-flex items-center justify-center size-9 shrink-0 rounded-md bg-transparent text-silver/85 cursor-pointer transition-colors duration-150 hover:text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-2"
        >
          <Menu className="size-5" aria-hidden="true" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-80 max-w-[85vw] gap-0 border-black/15 bg-[linear-gradient(180deg,var(--color-navbar-bg-1),var(--color-navbar-bg-2))] [&>button]:text-silver/85 [&>button:hover]:text-white"
      >
        <SheetHeader className="border-b border-white/10 px-4 py-4">
          <SheetTitle className="text-[12px] font-semibold uppercase tracking-[0.16em] text-silver/85">
            Navigation
          </SheetTitle>
          <SheetDescription className="sr-only">
            Main platform sections
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-3 pb-6">
          {visibleNavGroups(me.data).map((group) => (
            <nav key={group.label} aria-label={group.label}>
              <div className="px-3 pt-5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-silver/60">
                {group.label}
              </div>
              <div className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const locked = !item.soon && navTabLocked(item.to, onboarding);
                  // A note-less lock (the admin Simulators switch) renders as a
                  // quiet grey row: no Lock icon, no reason line.
                  const note = item.soon
                    ? 'Coming soon'
                    : locked
                      ? (cardGate(item.to, onboarding).note ?? null)
                      : null;

                  if (item.soon || locked) {
                    return (
                      <span
                        key={item.id}
                        className="flex flex-col gap-0.5 rounded-md px-3 py-2.5 border-l-2 border-transparent text-silver/40 cursor-not-allowed select-none"
                        aria-disabled="true"
                      >
                        <span className="flex items-center gap-2.5 text-[13.5px] font-medium">
                          <Icon className="size-4 shrink-0" aria-hidden="true" />
                          <span className="truncate">{item.label}</span>
                          {locked && note != null && (
                            <Lock className="size-3.5 shrink-0 ml-auto" aria-hidden="true" />
                          )}
                        </span>
                        <span className="pl-[26px] text-[11px] leading-snug text-silver/35 line-clamp-2">
                          {note != null ? <>{note} &mdash; </> : null}
                          {item.description}
                        </span>
                      </span>
                    );
                  }

                  return (
                    <Link
                      key={item.id}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className={cn(
                        'flex flex-col gap-0.5 rounded-md px-3 py-2.5 border-l-2 border-transparent no-underline text-silver/85 transition-colors duration-150 hover:text-white hover:bg-white/10',
                      )}
                      activeProps={{
                        className:
                          'flex flex-col gap-0.5 rounded-md px-3 py-2.5 border-l-2 border-electric no-underline text-white bg-white/10',
                      }}
                    >
                      <span className="flex items-center gap-2.5 text-[13.5px] font-medium">
                        <Icon className="size-4 shrink-0" aria-hidden="true" />
                        <span className="truncate">{item.label}</span>
                      </span>
                      <span className="pl-[26px] text-[11px] leading-snug text-silver/50 line-clamp-2">
                        {item.description}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </nav>
          ))}

          {showTermsLink && (
            <div className="mt-4 border-t border-white/10 pt-2">
              <Link
                to="/onboarding/terms"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 rounded-md px-3 py-2.5 border-l-2 border-transparent no-underline text-[13px] font-medium text-silver/70 transition-colors duration-150 hover:text-white hover:bg-white/10"
                activeProps={{
                  className:
                    'flex items-center gap-2.5 rounded-md px-3 py-2.5 border-l-2 border-electric no-underline text-[13px] font-medium text-white bg-white/10',
                }}
              >
                <ScrollText className="size-4 shrink-0" aria-hidden="true" />
                <span className="truncate">Terms &amp; Conditions</span>
              </Link>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
